import React, { useContext } from "react";

import BusinessIcon from "@mui/icons-material/Business";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { Clipboard } from "react-feather";
import { Link, useHistory } from "react-router-dom";

import { SubscriptionState } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { subscriptionTypeNameDisplayMapping } from "../../../constants/index";
import { subscriptionStateDisplayMapping } from "../../../constants/subscription";
import mainStyles from "../../../styles";
import { SearchTableBodyRowUserSearchProps } from "../../../types/search-display";
import UserOrgsCountChip from "components/shared/chip/user-orgs-count-chip";
import { SnackbarContext } from "context/snackbar-context";
import { getUserLastSeen } from "helpers/get-user-last-seen";
import useNavRedesign from "helpers/hooks/use-nav-redesign";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

const TableBodyRowUserSearch = ({
  row,
  regionCode,
}: SearchTableBodyRowUserSearchProps) => {
  const classes = mainStyles();
  const theme = useTheme();
  const history = useHistory();
  const navRedesign = useNavRedesign();

  const { copyToClipboard } = useContext(SnackbarContext);

  const userName = `${row.firstName} ${row.lastName}`;
  const userHref = `/user/${regionCode}/${row.id}`;
  const orgHref = `/organisation/${regionCode}/${row.orgId}`;

  const subscriptionStateChipStyleMap = {
    [SubscriptionState.UNSPECIFIED]: classes.neutral,
    [SubscriptionState.ACTIVE]: classes.greenDay,
    [SubscriptionState.TRIALING]: classes.blueMoon,
    [SubscriptionState.PAST_DUE]: classes.yellowSubmarine,
    [SubscriptionState.CANCELED]: classes.redWine,
  };

  // Provider-agnostic state chip (Chargify or Zuora), sourced from
  // subscriptionData. Free orgs report UNSPECIFIED and render no chip.
  const subscriptionStateChip = () => {
    const state = row.subscriptionData?.state;
    if (state !== undefined && state !== SubscriptionState.UNSPECIFIED) {
      return (
        <Chip
          size="small"
          color="primary"
          data-testid="subscription-state-chip"
          sx={{
            ...classes.spaceLeftXSmall,
            ...classes.spaceRightXSmall,
            ...subscriptionStateChipStyleMap[state],
          }}
          label={subscriptionStateDisplayMapping[state]}
          clickable={Boolean(row.orgId)}
          onClick={row.orgId ? () => history.push(orgHref) : undefined}
        />
      );
    }
  };

  const getSubscriptionTypeChip = () => {
    if (row.subscriptions.length === 0) {
      return (
        <Chip
          size="small"
          color="default"
          className={clsx(classes.spaceLeftXSmall, classes.spaceRightXSmall)}
          label={"Deactivated user"}
          data-testid="inactive-user-chip"
        />
      );
    }

    if (row.subscriptions.length === 1) {
      const type = row.subscriptions[0].type;
      const chipClass =
        type == SubscriptionType.FREE_ORG
          ? classes.yellowSubmarine
          : classes.purpleRain;
      return (
        <>
          <Chip
            size="small"
            color="primary"
            sx={{
              ...classes.spaceLeftXSmall,
              ...classes.spaceRightXSmall,
              ...chipClass,
            }}
            label={subscriptionTypeNameDisplayMapping[type]}
            data-testid="single-subscription-chip"
            clickable
            onClick={() => {
              if (row.orgId) {
                history.push(orgHref);
              }
            }}
          />
          {subscriptionStateChip()}
        </>
      );
    }

    return <></>;
  };

  const isActiveDisplay = () => (
    <span>
      {row.flags?.isActive ? " (Active User)" : " (Deactivated User)"}
    </span>
  );

  if (navRedesign) {
    return (
      <TableRow>
        <TableCell sx={{ border: 0, py: 0.75, px: 0 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Link
                    to={userHref}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <Typography component="span" sx={{ fontWeight: 700 }}>
                      {userName}
                    </Typography>
                  </Link>
                  {getSubscriptionTypeChip()}
                  <UserOrgsCountChip
                    orgsCount={row.orgsCount}
                    identityID={row.authId}
                  />
                  {row.orgName && (
                    <>
                      <Box
                        component="span"
                        sx={{ color: "text.disabled" }}
                        aria-hidden
                      >
                        |
                      </Box>
                      <Tooltip
                        title={row.orgId ? "Manage org" : ""}
                        disableHoverListener={!row.orgId}
                      >
                        <Box
                          component={row.orgId ? Link : "span"}
                          to={row.orgId ? orgHref : undefined}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                            color: "text.secondary",
                            fontSize: 14,
                            textDecoration: "none",
                            ...(row.orgId && {
                              cursor: "pointer",
                              "&:hover": {
                                color: "primary.main",
                                "& .org-name-text": {
                                  textDecorationStyle: "solid",
                                },
                              },
                            }),
                          }}
                          data-testid="user-search-org-name"
                        >
                          <BusinessIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                          <Box
                            component="span"
                            className="org-name-text"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              // Underline only the name so it reads as a link
                              // to the org, distinct from the plain user name
                              // beside it, which links to the user.
                              ...(row.orgId && {
                                textDecoration: "underline",
                                textDecorationStyle: "dotted",
                                textUnderlineOffset: 3,
                              }),
                            }}
                          >
                            {row.orgName}
                          </Box>
                        </Box>
                      </Tooltip>
                    </>
                  )}
                  {/* The first line has room to spare, so the user ID lives
                      here rather than behind the kebab - in the same run as the
                      name and org, not floated off next to the buttons. */}
                  <Box
                    component="span"
                    sx={{ color: "text.disabled" }}
                    aria-hidden
                  >
                    |
                  </Box>
                  <Tooltip title="Copy user ID">
                    <Box
                      component="span"
                      onClick={() => copyToClipboard(row.id)}
                      data-testid="user-search-user-id"
                      sx={{
                        ...classes.clickable,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                        cursor: "pointer",
                        color: "text.secondary",
                        fontSize: 12.5,
                        fontFamily: "ui-monospace, SFMono-Regular, monospace",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.id}
                      </Box>
                      <Clipboard size={12} style={{ flexShrink: 0 }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  <Box
                    component="span"
                    onClick={() => copyToClipboard(row.email)}
                    sx={{ ...classes.clickable, cursor: "pointer" }}
                  >
                    {row.email}{" "}
                    <Clipboard size={12} style={{ verticalAlign: "middle" }} />
                  </Box>
                  {" · Registered "}
                  {row.createdAt &&
                    formatDateTimeDisplay(
                      convertTimestampToDate(row.createdAt),
                    )}
                  {isActiveDisplay()}
                  {" · Last seen "}
                  {getUserLastSeen(row.lastSeen)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Link to={userHref} className="remove-link-style">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    data-anchor="user-manage-btn"
                  >
                    Manage User
                  </Button>
                </Link>
                {/* Both copy actions are now inline, so the kebab only ever
                    held this one destination — show it directly instead. */}
                {row.orgId && (
                  <Link to={orgHref} className="remove-link-style">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      data-testid="user-search-manage-org-btn"
                    >
                      Manage Org
                    </Button>
                  </Link>
                )}
              </Box>
            </Box>
          </Paper>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                paddingY: theme.spacing(0.5),
              }}
            >
              <Link
                to={userHref}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <span style={{ marginRight: theme.spacing(0.5) }}>
                  {userName}
                </span>
              </Link>{" "}
              {getSubscriptionTypeChip()}
              <UserOrgsCountChip
                orgsCount={row.orgsCount}
                identityID={row.authId}
              />
            </Typography>

            <div onClick={() => copyToClipboard(row.email)}>
              <Typography sx={classes.clickable} variant="body1" gutterBottom>
                {row.email}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>
            <div onClick={() => copyToClipboard(row.id)}>
              <Typography sx={classes.clickable} variant="body1">
                {row.id}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>

            <Typography variant="body1" paddingTop="5px">
              <b>Organisation name: </b>
              {row.orgName}{" "}
            </Typography>
            <Typography variant="body1" paddingTop="5px">
              <b>Date registered: </b>
              {row.createdAt &&
                formatDateTimeDisplay(
                  convertTimestampToDate(row.createdAt),
                )}{" "}
              {isActiveDisplay()}
            </Typography>
            <Typography variant="body1" paddingTop="5px">
              <b>Last seen: </b>
              {getUserLastSeen(row.lastSeen)}{" "}
            </Typography>
          </Grid>

          <Grid
            item
            container
            xs={6}
            direction="column"
            justifyContent="flex-end"
          >
            <Grid
              item
              container
              direction="row"
              justifyContent="flex-end"
              spacing={2}
            >
              <Grid item>
                <Link to={userHref} className="remove-link-style">
                  <Button
                    variant="contained"
                    color="primary"
                    data-anchor="user-manage-btn"
                  >
                    Manage User
                  </Button>
                </Link>
              </Grid>

              <Grid item>
                {row.orgId && (
                  <Link to={orgHref} className="remove-link-style">
                    <Button
                      variant="contained"
                      color="primary"
                      data-testid="user-search-manage-org-btn"
                    >
                      Manage Org
                    </Button>
                  </Link>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default TableBodyRowUserSearch;
